import { Injectable } from '@angular/core';
import { CopyConfigTypes, ModuleUniqueIds } from '@app/Constants';
import { Permissions } from '@app/Models/Permissions';
import { Tenant } from '@app/Models/tenant';
import { PermissionService, TenantService} from '@app/Services';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CategoryOptionType } from '../Enums';
import { CopyConfigExportImportRequest, CopyConfigExportOnlyRequest, CopyConfigImportOnlyRequest, Option, OptionCategory } from '../Models';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CopyConfigLog } from '../Models/copy-config-log';

@Injectable({
    providedIn: 'root',
})
export class CopyConfigService {
    optionsCategories: OptionCategory[] = [];
    copyConfigInProgress$ = new BehaviorSubject(false);
    copyConfigFinalised$ = new BehaviorSubject(false);
    copyConfigJobId$ = new BehaviorSubject<string | null>(null);
    sourceTenant$ = new BehaviorSubject<Tenant | null>(null);
    targetTenant$ = new BehaviorSubject<Tenant | null>(null);

    constructor(private readonly tenantService: TenantService, private readonly http: HttpClient, private readonly permissionService: PermissionService) { }

    loadDefaultOptionsCategories(): void {
        const canDoPiecewiseCopyConfig = this.permissionService.hasPermission(Permissions.piecewiseCopyConfig);
        this.optionsCategories = [];

        const moduleConfig: OptionCategory = {
            name: CategoryOptionType.ModuleConfig,
            tooltip: 'Copies over formflow configurations for chosen tenants. ' +
                'The selected modules\' draft configuration on the destination tenant is overwritten directly, discarding any unpublished draft changes there.',
            options: [],
            allowToggleAllOptions: true
        };
        const tenantConfig: OptionCategory = {
            name: CategoryOptionType.TenantConfig,
            tooltip: 'EHS tenant config',
            options: [],
            allowToggleAllOptions: true
        };
        const mandatoryProcesses: OptionCategory = {
            name: CategoryOptionType.MandatoryCopyProcesses,
            tooltip: 'Mandatory copy processes',
            options: [],
            allowToggleAllOptions: canDoPiecewiseCopyConfig
        };

        this.optionsCategories.push(moduleConfig);
        this.optionsCategories.push(tenantConfig);
        this.optionsCategories.push(mandatoryProcesses);

        tenantConfig.options.push(...this.createTenantConfigOptions());
        mandatoryProcesses.options.push(...this.createMandatoryCopyProcessesOptions());
    }

    hasCopyConfigStarted(): Observable<boolean> {
        return combineLatest([
            this.copyConfigInProgress$.asObservable(),
            this.copyConfigFinalised$.asObservable()]
        ).pipe(
            map(([copyConfigInProgress, copyConfigFinalised]) =>
                copyConfigInProgress || copyConfigFinalised
            )
        );
    }

    reset(): void {
        this.copyConfigInProgress$.next(false);
        this.copyConfigFinalised$.next(false);
        this.sourceTenant$.next(null);
        this.targetTenant$.next(null);
        this.clearState();
    }

    clearState(): void {
        this.optionsCategories = [];
    }

    getModuleConfigCategory(): OptionCategory {
        return this.optionsCategories.find(cateogory => cateogory.name === CategoryOptionType.ModuleConfig);
    }

    loadTenantModuleConfigOptions(copyFromTenantValue: string, copyToTenantValue: string): void {
        if (!!copyFromTenantValue && !!copyToTenantValue) {
            const sourceTenantAllowedModules$ = this.tenantService.getAllowedModules(copyFromTenantValue);
            const targetTenantAllowedModules$ = this.tenantService.getAllowedModules(copyToTenantValue);

            forkJoin([sourceTenantAllowedModules$, targetTenantAllowedModules$]).pipe(
                map(([selectedTenantAllowedModules, targetTenantAllowedModules]) =>
                    [selectedTenantAllowedModules ?? [], targetTenantAllowedModules ?? []])
            )
                .subscribe(
                    ([selectedTenantAllowedModules, targetTenantAllowedModules]) =>
                        this.populateModuleConfigOptions(selectedTenantAllowedModules, targetTenantAllowedModules)
                );
        } else {
            this.populateModuleConfigOptions([], []);
        }
    }

    loadTenantModuleConfigOptionsExportOnly(copyFromTenantValue: string): void {
        if (!!copyFromTenantValue) {
            this.tenantService.getAllowedModules(copyFromTenantValue).pipe(
                map((selectedTenantAllowedModules) => selectedTenantAllowedModules ?? [])
            ).subscribe((selectedTenantAllowedModules) =>
                this.populateModuleConfigOptionsExportOnly(selectedTenantAllowedModules)
            );
        } else {
            this.populateModuleConfigOptionsExportOnly([]);
        }
    }

    copyConfigExportImportRequest(copyFromTenantValue: string, copyToTenantValue: string): Observable<string> {
        const formFlowModuleIds = this.getModuleConfigCategory().options
            .filter((option) => option.Checked)
            .map((option) => option.FormFlowModuleId);
        const copyOptions = this.getSelectedCopyConfigOptions();

        const exportRequest: CopyConfigExportImportRequest = {
            SourceTenantName: copyFromTenantValue,
            TargetTenantName: copyToTenantValue,
            TargetTenantFullName: copyToTenantValue,
            CopyOptions: copyOptions,
            FormFlowModuleIds: formFlowModuleIds,
        };

        return this.tenantService
            .copyConfigExportImport(exportRequest).pipe(
                map((jobId) => jobId.toString())
            );
    }

    copyConfigExportOnlyRequest(copyFromTenantValue: string): Observable<string> {
        const formFlowModuleIds = this.getModuleConfigCategory().options
            .filter((option) => option.Checked)
            .map((option) => option.FormFlowModuleId);
        const copyOptions = this.getSelectedCopyConfigOptions();

        const exportRequest: CopyConfigExportOnlyRequest = {
            SourceTenantName: copyFromTenantValue,
            CopyOptions: copyOptions,
            FormFlowModuleIds: formFlowModuleIds,
        };

        return this.tenantService
            .copyConfigExportOnly(exportRequest).pipe(
                map((jobId) => jobId.toString())
            );
    }

    copyConfigImportOnlyRequest(copyToTenantValue: string, importId: string): Observable<string> {
        const importRequest: CopyConfigImportOnlyRequest = {
            TargetTenantName: copyToTenantValue,
            ImportId: importId
        };

        return this.tenantService
            .copyConfigImportOnly(importRequest).pipe(
                map((jobId) => jobId.toString())
            );
    }

    public getCopyConfigLogs(tenantId: string): Observable<CopyConfigLog[]> {
        return this.http.get<CopyConfigLog[]>('/api/tenant/audit/log?tenantId=' + tenantId);
    }

    public getModulesWithChanges(tenantId: string, moduleIds: string[]): Observable<string[]> {
        if (!tenantId || !moduleIds?.length) {
            return of([]);
        }

        let params = new HttpParams().set('tenantId', tenantId);
        moduleIds.forEach((moduleId) => {
            params = params.append('moduleIds', moduleId);
        });

        return this.http.get<string[]>('/api/tenant/copyconfig/modules-with-changes', { params });
    }

    public updateCopyConfigLog(log: CopyConfigLog): Observable<CopyConfigLog> {
        return this.http.put<CopyConfigLog>('/api/tenant/audit/log/' + log.Id, log);
    }

    private populateModuleConfigOptions(
        sourceTenantAllowedModules: string[],
        targetTenantAllowedModules: string[]
    ): void {
        const allowedModuleOptions = this.getModuleOptionsSourceAndTargetTenantsHaveInCommon(
            sourceTenantAllowedModules,
            targetTenantAllowedModules,
            1
        );
        const moduleConfigCategory = this.getModuleConfigCategory();
        moduleConfigCategory.options = [...allowedModuleOptions];
    }

    private populateModuleConfigOptionsExportOnly(sourceTenantAllowedModules: string[]): void {
        const allowedModuleOptions = this.getModuleOptionsSourceTenant(sourceTenantAllowedModules);
        const moduleConfigCategory = this.getModuleConfigCategory();
        moduleConfigCategory.options = [...allowedModuleOptions];
    }

    private getModuleOptionsSourceTenant(sourceTenantAllowedModules: string[]): Option[] {
        let order = 1;
        return sourceTenantAllowedModules
            .filter(module => module.toString() !== ModuleUniqueIds.Admin)
            .map(moduleUniqueId => {
                const newModule: Option = {
                    Identifier: CopyConfigTypes.FormFlow,
                    Text: moduleUniqueId,
                    Checked: true,
                    Editable: true,
                    Order: order,
                    FormFlowModuleId: moduleUniqueId,
                };
                order++;
                return newModule;
            });
    }

    private getModuleOptionsSourceAndTargetTenantsHaveInCommon(
        sourceTenantAllowedModules: string[],
        targetTenantAllowedModules: string[],
        startingOrder: number
    ): Option[] {
        const allowedModulesSourceAndTargetHaveInCommon =
            this.filterAllowedModulesSourceAndTargetTenantHaveInCommon(
                sourceTenantAllowedModules,
                targetTenantAllowedModules
            );

        let order = startingOrder;
        const allowedModuleOptions: Option[] = allowedModulesSourceAndTargetHaveInCommon
            .map(moduleUniqueId => {
                const newModule: Option = {
                    Identifier: CopyConfigTypes.FormFlow,
                    Text: moduleUniqueId,
                    Checked: true,
                    Editable: true,
                    Order: order,
                    FormFlowModuleId: moduleUniqueId,
                };
                order++;
                return newModule;
            });

        return allowedModuleOptions;
    }

    private filterAllowedModulesSourceAndTargetTenantHaveInCommon(
        sourceTenantAllowedModules: string[],
        targetTenantAllowedModules: string[]
    ): string[] {
        return sourceTenantAllowedModules.filter(
            (module) =>
                targetTenantAllowedModules.includes(module) &&
                module.toString() !== ModuleUniqueIds.Admin
        );
    }

    private distinct = (value, index, self) => self.indexOf(value) === index;

    private getSelectedCopyConfigOptions(): CopyConfigTypes[] {
        const selectedOptions = this.optionsCategories
            .reduce<Option[]>((options, category) => [...options, ...category.options], [])
            .filter((option) => option.Checked)
            .map((option) => option.Identifier)
            .filter(this.distinct);

        return this.addExtraOptions(selectedOptions);
    }

    private addExtraOptions(options: CopyConfigTypes[]): CopyConfigTypes[] {
        if (options.includes(CopyConfigTypes.Permissions) && options.includes(CopyConfigTypes.PermissionGroups)) {
            options.push(CopyConfigTypes.PermissionGroupsTemplateFilters);
        }
        return options;
    }

    private createTenantConfigOptions(): Option[] {
        const severityMatrix: Option = {
            Identifier: CopyConfigTypes.SeverityMatrix,
            Text: 'Severity Matrix',
            Checked: true,
            Editable: true,
            Order: 1,
            FormFlowModuleId: undefined,
            Tooltip: 'Copies severity matrices from the source tenant. ' +
                'Existing matrices on the destination are updated, new ones are added, ' +
                'and matrices not present on the source are removed'
        };
        const feeds: Option = {
            Identifier: CopyConfigTypes.Feeds,
            Text: 'Feeds',
            Checked: true,
            Editable: true,
            Order: 2,
            FormFlowModuleId: undefined,
            Tooltip: 'Copies over feed configuration from one tenant to another. ' +
                'Copied feeds will be set up in a new location, borrowing some of the conventions from the source. Example:\n\n' +
                '\\\\eu-avaprod-util\\feeds\\[Source Tenant]\\modulerecordfeed\nBecomes\n' +
                '\\\\eu-avaprod-utilNfeeds\\[Target Tenant]\\modulerecordfeeds\n\n' +
                'Module record feeds and HR feeds will only work if you are copying the ' +
                'relevant module configuration as well - else the mappings will likely be broken'
        };
        const templates: Option = {
            Identifier: CopyConfigTypes.Templates,
            Text: 'Templates',
            Checked: true,
            Editable: true,
            Order: 3,
            FormFlowModuleId: undefined,
            Tooltip: 'Copies created templates and their content for applicable modules'
        };
        const tenantStatus: Option = {
            Identifier: CopyConfigTypes.TenantStatus,
            Text: 'Tenant Status',
            Checked: true,
            Editable: true,
            Order: 4,
            FormFlowModuleId: undefined,
            Tooltip: 'Copies the ACT defined tenant status. Note that these drive permissions in ACT, ' +
                'some people may lose access to the tenant in ACT\n' +
                'Does not affect the EHS users of the tenant'
        };
        const actionRemindersConfiguration: Option = {
            Identifier: CopyConfigTypes.ModuleConfig,
            Text: 'Action Reminders Configuration',
            Checked: true,
            Editable: true,
            Order: 5,
            FormFlowModuleId: undefined,
            Tooltip: 'Copies the Action reminders configuration.'
        };
        const heirarchyAndAttributes: Option = {
            Identifier: CopyConfigTypes.HierarchyAndAttributes,
            Text: 'Hierarchy & Attributes',
            Checked: false,
            Editable: true,
            Order: 6,
            FormFlowModuleId: undefined,
            Tooltip: 'Copies the Hierarchy structure and any attributes attached to any locations\n' +
                'This can make existing data inaccessible'
        };
        const permissionGroups: Option = {
            Identifier: CopyConfigTypes.PermissionGroups,
            Text: 'Permission Groups',
            Checked: false,
            Editable: true,
            Order: 7,
            FormFlowModuleId: undefined,
            Tooltip: 'Copies the Permission Groups assigned in EHS, but not any assignment of ' +
                'them to Users or Hierarchy nodes\n' +
                'This can make existing data inaccessible'
        };
        const featureFlags: Option = {
            Identifier: CopyConfigTypes.FeatureFlags,
            Text: 'Feature Flags',
            Checked: false,
            Editable: true,
            Order: 8,
            FormFlowModuleId: undefined,
            Tooltip: 'Sets all the feature flags identical to the source tenant\n' +
                'This can affect the access or behaviour of certain features'
        };

        return [severityMatrix, feeds, templates, tenantStatus, actionRemindersConfiguration,
            heirarchyAndAttributes, permissionGroups, featureFlags];
    }

    private createMandatoryCopyProcessesOptions(): Option[] {
        const canDoPiecewiseCopyConfig = this.permissionService.hasPermission("Piecewise Copy Config");
        
        const emails: Option = {
            Identifier: CopyConfigTypes.EmailRules,
            Text: 'Emails Rules',
            Checked: true,
            Editable: canDoPiecewiseCopyConfig,
            Order: 1,
            FormFlowModuleId: undefined,
        };
        const states: Option = {
            Identifier: CopyConfigTypes.StateRules,
            Text: 'State Rules',
            Checked: true,
            Editable: canDoPiecewiseCopyConfig,
            Order: 2,
            FormFlowModuleId: undefined,
        };
        const rulesId: Option = {
            Identifier: CopyConfigTypes.RulesId,
            Text: 'Rules Id',
            Checked: true,
            Editable: canDoPiecewiseCopyConfig,
            Order: 3,
            FormFlowModuleId: undefined,
        };
        const lists: Option = {
            Identifier: CopyConfigTypes.Lists,
            Text: 'Lists & Labels',
            Checked: true,
            Editable: canDoPiecewiseCopyConfig,
            Order: 4,
            FormFlowModuleId: undefined,
        };
        const permissions: Option = {
            Identifier: CopyConfigTypes.Permissions,
            Text: 'Update Permissions',
            Checked: true,
            Editable: canDoPiecewiseCopyConfig,
            Order: 5,
            FormFlowModuleId: undefined,
        };
        const release: Option = {
            Identifier: CopyConfigTypes.Release,
            Text: 'Create Release',
            Checked: true,
            Editable: canDoPiecewiseCopyConfig,
            Order: 6,
            FormFlowModuleId: undefined,
        };
        const publish: Option = {
            Identifier: CopyConfigTypes.Publish,
            Text: 'Publish Release',
            Checked: true,
            Editable: canDoPiecewiseCopyConfig,
            Order: 7,
            FormFlowModuleId: undefined,
        };

        return [emails, states, rulesId, lists, permissions, release, publish];
    }
}
